// The only place this module talks to the marketing-analytics read API
// (`marketing_analytics/profit_yield/read_api.py`, merged as PR #85). Every
// response is typed as the S8 envelope, so `meta` is never optional — a
// component that could render without freshness metadata does not typecheck.
//
// The portal is not a backend (I-4). Nothing here recomputes a figure: the
// producer owns every GCS object key and every number in it, and this module
// only reads what the artefact already says.

import type { GrainValue } from "@/lib/time-range";

import type { Band, Enveloped, Region } from "@/lib/meteoric-regions";

// The S8 envelope, the region set and the band vocabulary are shared with the
// other UK / EU / MENA module, so they live in one place.
export { isRegion, REGIONS } from "@/lib/meteoric-regions";
export type { Band, Enveloped, FreshnessMeta, Region } from "@/lib/meteoric-regions";
export type Verdict = "scale" | "hold" | "cut" | "insufficient-data";
export type Maturity = "closed" | "provisional";
export type AttentionSeverity = "critical" | "warning" | "info";

/**
 * One dated point of a ratio series.
 *
 * `present: false` marks a period the source query had no row for — the value is
 * zero-filled, not observed. Charting a zero-fill as if it were a measurement is
 * how a gap in collection reads as a collapse in performance, so every consumer
 * here drops non-present points rather than plotting them.
 */
export type SeriesPoint = {
  m: string;
  v: number;
  present: boolean;
  /**
   * Additive totals behind the ratio (producer PR marketing-analytics#98). A
   * window's figures are their SUM - see `window.ts`. Optional because an
   * artefact written before that PR carries none; `null` means the source had
   * no such total for this bucket (the monthly rollup has no units).
   */
  profit_gbp?: number | null;
  spend_gbp?: number | null;
  attributed_sales_gbp?: number | null;
  attributed_units?: number | null;
  exact_sales_gbp?: number | null;
  /**
   * Monthly points only: the month has ended but is still inside the 14-day
   * attribution maturity window, so its figures can still move (bug 3f30402f).
   * Absent on artefacts written before the producer shipped it.
   */
  provisional?: boolean;
};

export type Tile = {
  kpi_id: string;
  kpi_name: string;
  value: number | null;
  unit: string;
  delta_vs_prior_period: number | null;
  band: Band;
  period: string | null;
  sparkline_values: number[];
  series: SeriesPoint[];
  series_daily: SeriesPoint[];
  series_weekly: SeriesPoint[];
  series_monthly: SeriesPoint[];
};

export type Secondary = {
  basis: string;
  profit_gbp: number | null;
  profit_per_pound: number | null;
  spend_gbp: number | null;
  attributed_sales_gbp: number | null;
  attributed_units: number | null;
  coverage_pct: number | null;
  maturity: Maturity;
};

export type AttentionItem = {
  severity: AttentionSeverity;
  headline: string;
  deep_link: string;
};

export type Overview = {
  generated_at: string | null;
  r2_age_seconds: number | null;
  tile: Tile;
  secondary: Secondary;
  attention_items: AttentionItem[];
};

export type CampaignRow = {
  campaign_id: string;
  name: string;
  ad_product: string;
  marketplace: string;
  product: string | null;
  variant: string | null;
  variant_count: number;
  spend_gbp: number | null;
  attr_sales_gbp: number | null;
  attr_units: number | null;
  profit_gbp: number | null;
  profit_per_pound: number | null;
  coverage_pct: number | null;
  verdict: Verdict;
};

/** The campaigns beyond the ranked page, as a count and two totals — never as rows. */
export type Tail = {
  count: number;
  spend_gbp: number | null;
  profit_gbp: number | null;
};

/** One additive (bucket, campaign) row; a window's campaign table is their sum. */
export type GrainCampaignRow = {
  bucket: string;
  campaign_id: string;
  name: string;
  ad_product: string;
  marketplace: string;
  product: string | null;
  variant: string | null;
  variant_count: number;
  spend_gbp: number | null;
  attr_sales_gbp: number | null;
  attr_units: number | null;
  profit_gbp: number | null;
  exact_sales_gbp: number | null;
};

export type RegionDetail = Omit<Overview, "r2_age_seconds"> & {
  region: string;
  campaigns: CampaignRow[];
  tail: Tail;
  campaigns_daily?: GrainCampaignRow[];
  campaigns_weekly?: GrainCampaignRow[];
};

export const COST_STACK_COMPONENTS = [
  "ads_spend",
  "cogs",
  "fulfilment",
  "cogs_and_fulfilment_unsplit",
  "refunds",
  "discounts",
  "net_contribution_profit",
] as const;

export type CostStackComponent = (typeof COST_STACK_COMPONENTS)[number];
/** One grain vocabulary with the page Period (bug 3f30402f-c6c9-81e8). */
export type CostStackGrain = GrainValue;
export type CostStackMode = "per_unit" | "pct_of_sales";

export type CostStackBucket = {
  bucket: string;
  present: boolean;
  attributed_units: number;
  attributed_sales_devat_gbp: number;
  identity_delta_gbp: number;
  /** Monthly buckets only: ended but inside the 14-day attribution window (bug 3f30402f). */
  provisional?: boolean;
} & Record<CostStackComponent, number | null>;

export type CostStackCoverage = {
  margin_split_coverage_pct: number | null;
  leakage_coverage_pct: number | null;
  no_rate_sales_pct: number | null;
  rates_as_of: string | null;
};

export type CostStack = {
  version: "v1";
  generated_at: string | null;
  age_seconds: number | null;
  grain: CostStackGrain;
  region: string;
  components: CostStackComponent[];
  /**
   * Whether the components add back to attributed sales. `false` means the
   * stack does not reconcile, and the surface SAYS so rather than drawing a
   * tidy chart of numbers that do not balance.
   */
  identity_ok: boolean;
  coverage: CostStackCoverage;
  modes: Record<CostStackMode, { unit: string; buckets: CostStackBucket[] }>;
  basis_note: string;
};

/** Per-region latest data date, from `/freshness`. */
export type Freshness = Record<string, string | null>;

// Same-origin via the Pages Function proxy (functions/meteoric-profit-yield/[[path]].ts),
// following the routing decision that everything goes through os.ketosource.co
// (Damien, 2026-09-02) — no second hostname, no CORS preflight, no cookie config.
const BASE = import.meta.env.VITE_PROFIT_YIELD_API_BASE ?? "/meteoric-profit-yield/v1";

export class ProfitYieldError extends Error {
  constructor(
    readonly status: number,
    readonly body: unknown,
  ) {
    super(`Profit Yield API ${status}`);
  }
}

async function safeBody(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

async function get<T>(path: string): Promise<Enveloped<T>> {
  const response = await fetch(`${BASE}${path}`, {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (!response.ok) {
    throw new ProfitYieldError(response.status, await safeBody(response));
  }
  return (await response.json()) as Enveloped<T>;
}

/**
 * The overview artefact is the producer's MULTI-PLUGIN envelope
 * (`{ generated_at, r2_age_seconds, plugins: [{ id: "profit-yield", tile, … }] }`),
 * not a flat `Overview`. Reading `tile` off the envelope directly left it
 * undefined and crashed the tab. This unwraps the `profit-yield` plugin — reading
 * fields, never deriving one — the same way Units Sold's `pluginFrom` does. A
 * payload with no such plugin is a 404-equivalent statement, not a blank.
 */
type OverviewEnvelope = {
  generated_at?: string | null;
  r2_age_seconds?: number | null;
  plugins?: unknown[];
};

export async function getOverview(): Promise<Enveloped<Overview>> {
  const envelope = await get<OverviewEnvelope>("/overview");
  const found = (envelope.data.plugins ?? []).find(
    (entry): entry is Record<string, unknown> =>
      typeof entry === "object" && entry !== null && (entry as { id?: unknown }).id === "profit-yield",
  );
  if (!found) {
    throw new ProfitYieldError(404, { error: "plugin_missing", detail: "no profit-yield plugin" });
  }
  return {
    meta: envelope.meta,
    data: {
      generated_at: envelope.data.generated_at ?? null,
      r2_age_seconds: envelope.data.r2_age_seconds ?? null,
      tile: found.tile as Tile,
      secondary: found.secondary as Secondary,
      attention_items: (found.attention_items as AttentionItem[]) ?? [],
    },
  };
}

export const getRegion = (region: Region) => get<RegionDetail>(`/regions/${region}`);

export const getFreshness = () => get<Freshness>("/freshness");

export const getCostStack = (grain: CostStackGrain, region?: Region) => {
  const query = new URLSearchParams({ grain });
  if (region) query.set("region", region);
  return get<CostStack>(`/cost-stack?${query.toString()}`);
};

/**
 * One message per API failure. A 404 here is a genuine statement — the daily
 * aggregation has not produced this artefact yet — and is deliberately NOT
 * folded into "something went wrong": "not computed yet" and "we could not
 * read it" are different facts, and only one of them is worth paging someone
 * about.
 *
 * A non-`ProfitYieldError` (a network failure, an unconfigured proxy) degrades
 * to the same stated-unknown as a 503 — never to a blank or an empty chart,
 * which would assert the false claim "there is nothing".
 *
 * `areaName` comes from the registry via the shell (I-5). Hardcoding "Customer
 * Acquisition" would leave the 403 naming the wrong area the day this module is
 * reassigned, so the name is not a literal a module is allowed to hold.
 */
export function messageFor(error: unknown, areaName: string): string {
  if (!(error instanceof ProfitYieldError)) {
    return "Profit Yield could not be read, so nothing is shown. Try again shortly.";
  }
  switch (error.status) {
    case 400:
      return "That region or period is not one this dashboard reports on.";
    case 401:
      return "Your session could not be confirmed. Reload the page to sign in again.";
    case 403:
      return `You do not have permission to see this. Ask the ${areaName} area owner.`;
    case 404:
      return "Profit Yield has not been computed yet. The first daily aggregation has not run.";
    case 502:
      return "The analytics store could not be read — credentials, throttling, or a transient error, not a missing dataset. Retry shortly.";
    case 503:
      return "Profit Yield is not configured on the server, so nothing is shown.";
    default:
      return "Profit Yield could not be read, so nothing is shown. Try again shortly.";
  }
}
